import React from 'react';
import ShipConfirmSummary from './ShipConfirmSummary';
import { todayInTenant, formatDate } from '../../shared/tenantClock';
import DateInput from '../../shared/DateInput';
import ShipmentCarriageFields, { carriageFromOrder } from './ShipmentCarriageFields';
import PackageDetailsFields from './PackageDetailsFields';
import ActualPostageField from './ActualPostageField';
import { shippablePackages, contentsSummary, detailsDraft, allPackages } from './packageShared';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// A soft warning under a Date Shipped earlier than the Order Date or in the
// future (issue #292) — usually a typo, but the user can save past it (see
// ui-architecture.md's Soft Warning, Not Hard Block).
export function ShipDateWarning({ date, orderDate }) {
  if (!date) return null;
  let message = null;
  if (orderDate && date < orderDate) message = `This is before the Order Date (${formatDate(orderDate)}).`;
  else if (date > todayInTenant()) message = 'This is in the future.';
  if (!message) return null;
  return (
    <div style={{ fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
      {message} Check the date — you can still save it.
    </div>
  );
}

// `order` seeds the shipment's Carrier/Service/Options from the order's
// own (issue #281), whatever its type. Every outermost package that can
// ship starts ticked (issues #299, #313), each with its saved details
// (`details`, by package id) and room for its actual postage (`postage`,
// by package id, inc GST — issue #350).
export function blankShipFields(order, packages) {
  const shippable = shippablePackages(packages);
  return {
    shipped_date: todayInTenant(),
    tracking_number: '',
    package_ids: shippable.map((p) => String(p.id)),
    details: Object.fromEntries(shippable.map((p) => [String(p.id), detailsDraft(p)])),
    postage: {},
    ...carriageFromOrder(order),
  };
}

// The Ship summary for the packages chosen (issue #299): each line ships
// what's in them. `lines` are the order's lines as /api/fulfilment
// returns them.
export function shipRowsFor(lines, chosen) {
  const inPackage = new Map();
  for (const pkg of chosen || []) {
    for (const c of pkg.contents || []) inPackage.set(String(c.line_id), (inPackage.get(String(c.line_id)) || 0) + Number(c.qty));
  }
  return (lines || []).map((l) => {
    const qty = Number(l.qty) || 0;
    const shipped = Number(l.qty_shipped || 0);
    const shippable = inPackage.get(String(l.id)) || 0;
    const backorder = Math.max(0, qty - (shipped + shippable));
    const packedUnshipped = Math.max(0, Number(l.qty_packed || 0) - shipped);
    return {
      key: l.id,
      sku: l.product?.sku,
      name: l.product?.name,
      qty,
      shippable,
      backorder,
      outcome: shippable <= 0 ? 'none' : backorder > 0 ? 'partial' : 'full',
      noneText: shippable <= 0 && packedUnshipped > 0 ? 'In a package not shipping' : undefined,
    };
  });
}

// The fields every Ship pass collects — which outermost packages (each
// with its Packaging/Dimensions/Weight and Actual Postage first and the packages inside it
// listed beneath; untick any staying behind), the Ready to Ship summary,
// then Date/Carrier/Service/Options/Tracking# —
// shared by every entry point that ships an order (SalesOrderPage's plain
// Ship button, PickOrderPage's Ship Order, and ShipmentsTable's own Add
// Shipment drawer) so the field set and its validation only exist in one
// place (unify-ship). Controlled — the caller owns `value`/`onChange` since
// ShipmentsTable needs these fields alongside its own order-search step,
// inside its existing drawer, rather than a separate dialog the other two
// callers use (see ShipConfirmDialog.js).
function ShipConfirmFields({ lines, packages, value, onChange, orderDate }) {
  const shippable = shippablePackages(packages);
  const ids = value.package_ids || [];
  const chosen = shippable.filter((p) => ids.includes(String(p.id)));
  const rows = shipRowsFor(lines, chosen);

  const setField = (key, val) => onChange({ ...value, [key]: val });
  const toggle = (pkg) => {
    const id = String(pkg.id);
    onChange({ ...value, package_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };
  const setDetails = (pkg, next) => onChange({ ...value, details: { ...(value.details || {}), [String(pkg.id)]: next } });
  const setPostage = (pkg, next) => onChange({ ...value, postage: { ...(value.postage || {}), [String(pkg.id)]: next } });

  return (
    <>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>{shippable.length === 1 ? 'Package' : 'Packages'}</label>
        {shippable.length === 0 && <div style={{ fontSize: 13, color: '#888' }}>Nothing packed is ready to ship.</div>}
        {shippable.map((p) => {
          const ticked = ids.includes(String(p.id));
          const inside = allPackages(p.packages);
          return (
            <div key={p.id} style={{ border: '1px solid #e3e7ee', borderRadius: 6, padding: '8px 10px', marginBottom: 8, background: ticked ? '#fff' : '#f6f8fa' }}>
              <label style={{ display: 'flex', alignItems: 'baseline', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                <input type="checkbox" checked={ticked} onChange={() => toggle(p)} />
                <span>{p.name}{p.open ? ' (open)' : ''}</span>
              </label>
              {ticked && (
                <div style={{ marginTop: 8 }}>
                  <PackageDetailsFields value={(value.details || {})[String(p.id)] || detailsDraft(p)} onChange={(next) => setDetails(p, next)} />
                  <ActualPostageField value={(value.postage || {})[String(p.id)]} onChange={(next) => setPostage(p, next)} />
                </div>
              )}
              {(p.goods || []).length > 0 && <div style={{ fontSize: 12, color: '#555' }}>{contentsSummary(p.goods)}</div>}
              {inside.map((inner) => (
                <div key={inner.id} style={{ fontSize: 12, color: '#555', marginTop: 4, paddingLeft: 10, borderLeft: '2px solid #e3e7ee' }}>
                  <span style={{ fontWeight: 600 }}>{inner.label}</span>
                  {(inner.goods || []).length > 0 && `: ${contentsSummary(inner.goods)}`}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {chosen.length > 0 && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Ready to Ship</label>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <ShipConfirmSummary rows={rows} />
          </div>
        </div>
      )}
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Date Shipped</label>
        <DateInput
          className="styled-input"
          value={value.shipped_date}
          onChange={(e) => setField('shipped_date', e.target.value)}
          style={{ width: '100%', fontSize: 14 }}
        />
        <ShipDateWarning date={value.shipped_date} orderDate={orderDate} />
      </div>
      <ShipmentCarriageFields
        value={{ carrier_id: value.carrier_id || '', shipping_service_id: value.shipping_service_id || '', shipping_option_ids: value.shipping_option_ids || [] }}
        onChange={(carriage) => onChange({ ...value, ...carriage })}
      />
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Tracking #</label>
        <input
          className="styled-input"
          placeholder="Tracking #"
          value={value.tracking_number}
          onChange={(e) => setField('tracking_number', e.target.value)}
          style={{ width: '100%', fontSize: 14 }}
        />
      </div>
    </>

  );
}

export default ShipConfirmFields;
